/**
 * NCEMI-specific form state sub-hook.
 * Manages: funding shares, brand, bank cards, tenures, price leading guardrail.
 *
 * This hook has two useEffect hooks that share some dependencies (config, campaignType, mode, initialData):
 *   1. **Config initialisation** — sets role-based funding-share defaults and the default brand name
 *      when creating a new campaign. Both need the same guard (config present, NCEMI type, create mode)
 *      and are therefore co-located in a single effect.
 *   2. **InitialData population** — populates all NCEMI fields from the API response when editing or
 *      viewing an existing campaign. This is intentionally kept separate because it runs in a different
 *      mode (modify/view) and has different early-return conditions.
 */

import { useState, useMemo, useEffect } from 'react';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { CampaignType, FundingShareType, PaymentInstrumentType, UserRole } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { FormMode } from 'src/components/organisms/CampaignForm/types';
import { CampaignData } from 'src/@types/valueMatching/campaign';

export interface UseNcemiFormStateParams {
    /** Campaign configuration fetched from the API; null while loading or on error. */
    config: CampaignConfigurationData | null;
    /** The resolved campaign type. This hook only activates when the type is NO_COST_EMI. */
    campaignType: CampaignType | undefined;
    /** Current form mode — determines whether fields are editable and how initialisation works. */
    mode: FormMode;
    /** Pre-existing campaign data used to populate fields in modify/view mode. Ignored in create mode. */
    initialData?: CampaignData;
}

export function useNcemiFormState({
    config,
    campaignType,
    mode,
    initialData,
}: UseNcemiFormStateParams) {
    // ── State ───────────────────────────────────────────────────────────
    // Shares are stored as numbers for easier validation; converted to string only for display.
    const [sellerShare, setSellerShare] = useState<number | undefined>(undefined);
    const [brandShare, setBrandShare] = useState<number | undefined>(undefined);
    const [bankShare, setBankShare] = useState<number | undefined>(undefined);
    const [amazonShare, setAmazonShare] = useState<number | undefined>(undefined);

    const [brandName, setBrandName] = useState('');
    const [selectedCreditCards, setSelectedCreditCards] = useState<string[]>([]);
    const [selectedDebitCards, setSelectedDebitCards] = useState<string[]>([]);
    const [selectedCbccCards, setSelectedCbccCards] = useState<string[]>([]);
    const [selectedBflCards, setSelectedBflCards] = useState<string[]>([]);
    // Tenures are stored as strings because they map to select-option values from the API config.
    const [creditCardTenure, setCreditCardTenure] = useState('');
    const [debitCardTenure, setDebitCardTenure] = useState('');
    const [cbccCardTenure, setCbccCardTenure] = useState('');
    const [bflCardTenure, setBflCardTenure] = useState('');
    const [priceLeadingGuardrail, setPriceLeadingGuardrail] = useState('');

    // ── Bank card options ────────────────────────────────────────────────
    const cardConfig = config?.campaignTypeConfiguration?.noCostEMI;

    // ── Initialise funding shares from role + default brand name ─────────
    // Combined into one effect because both need the same guard conditions.
    useEffect(() => {
        if (!config || campaignType !== CampaignType.NO_COST_EMI) return;
        if (mode !== FormMode.CREATE && initialData) return;

        // Role-based funding share defaults
        const role = config.role;
        if (role === UserRole.SELLER) {
            // Seller: all shares start at 0% — seller can redistribute across seller/brand/amazon
            setSellerShare(0); setBrandShare(0); setBankShare(0); setAmazonShare(0);
        } else if (role === UserRole.BRAND) {
            // Brand: locked at 100% brand share, others 0%
            setSellerShare(0); setBrandShare(100); setBankShare(0); setAmazonShare(0);
        } else if (role === UserRole.AMAZON) {
            // Amazon: defaults to 100% amazon share, can redistribute across all
            setSellerShare(0); setBrandShare(0); setBankShare(0); setAmazonShare(100);
        }

        // Default brand name from config
        setBrandName(cardConfig?.defaultBrandName ?? '');

        // Default tenure: smallest numeric tenure per card type.
        // Note: Object.keys() sorts integer-like string keys in ascending numeric order
        // per the ECMAScript spec, so this picks the smallest tenure (e.g., '3' from { '12': ..., '3': ... }).
        const firstTenure = (tenures: Record<string, string> | undefined): string =>
            Object.keys(tenures ?? {})[0] ?? '';
        setCreditCardTenure(firstTenure(cardConfig?.creditCard?.allowedTenures));
        setDebitCardTenure(firstTenure(cardConfig?.debitCard?.allowedTenures));
        setCbccCardTenure(firstTenure(cardConfig?.cbccCard?.allowedTenures));
        setBflCardTenure(firstTenure(cardConfig?.bflCard?.allowedTenures));
    }, [config, campaignType, mode, initialData, cardConfig]);

    // ── Populate from initialData (modify / view) ───────────────────────
    useEffect(() => {
        if (mode === FormMode.CREATE || !initialData) return;
        if (campaignType !== CampaignType.NO_COST_EMI) return;

        // Funding shares
        const shares = initialData.attributes?.noCostEMI?.fundingShareByFundingShareType;
        if (shares) {
            setSellerShare(shares[FundingShareType.SELLER] ?? undefined);
            setBrandShare(shares[FundingShareType.BRAND] ?? undefined);
            setBankShare(shares[FundingShareType.BANK] ?? undefined);
            setAmazonShare(shares[FundingShareType.AMAZON] ?? undefined);
        }

        // Brand name
        if (initialData.attributes?.noCostEMI?.brandAttributes?.brandName) {
            setBrandName(initialData.attributes.noCostEMI.brandAttributes.brandName);
        }

        // Price leading guardrail
        if (initialData.guardrails?.noCostEMI?.amazonPriceLeadingGuardrail != null) {
            setPriceLeadingGuardrail(String(initialData.guardrails.noCostEMI.amazonPriceLeadingGuardrail));
        }

        // Normalize API display names to config code keys.
        const buildNameToCodeMap = (codes: Record<string, string> | undefined): Map<string, string> => {
            const map = new Map<string, string>();
            if (codes) {
                for (const [code, displayName] of Object.entries(codes)) {
                    map.set(displayName, code);
                }
            }
            return map;
        };
        const creditNameToCode = buildNameToCodeMap(cardConfig?.creditCard?.codes);
        const debitNameToCode = buildNameToCodeMap(cardConfig?.debitCard?.codes);
        const cbccNameToCode = buildNameToCodeMap(cardConfig?.cbccCard?.codes);
        const bflNameToCode = buildNameToCodeMap(cardConfig?.bflCard?.codes);

        /** Normalize a list of bank identifiers (which may be codes or display names) to codes. */
        const normalizeToCodes = (values: string[], nameToCode: Map<string, string>): string[] =>
            values.map((val) => nameToCode.get(val) ?? val);

        // Card selections and tenures from payment instrument guardrail
        const combinations = initialData.guardrails?.noCostEMI
            ?.paymentInstrumentBankTenureGuardrail
            ?.paymentInstrumentBasedBankTenureCombinations ?? [];

        for (const instrumentCombo of combinations) {
            const bankNames = (instrumentCombo.bankTenureCombinations ?? []).map(
                (bankTenure) => bankTenure.bankName,
            );
            const allTenures = (instrumentCombo.bankTenureCombinations ?? []).reduce<number[]>(
                (accumulated, bankTenure) => accumulated.concat(bankTenure.tenures ?? []), [],
            );
            const maxTenure = Math.max(...allTenures, 0);
            const tenureStr = maxTenure > 0 ? String(maxTenure) : '';

            switch (instrumentCombo.paymentInstrumentType) {
                case PaymentInstrumentType.CREDIT_CARD:
                    setSelectedCreditCards(normalizeToCodes(bankNames, creditNameToCode));
                    setCreditCardTenure(tenureStr);
                    break;
                case PaymentInstrumentType.DEBIT_CARD:
                    setSelectedDebitCards(normalizeToCodes(bankNames, debitNameToCode));
                    setDebitCardTenure(tenureStr);
                    break;
                case PaymentInstrumentType.FLIPKART_CARD:
                    setSelectedCbccCards(normalizeToCodes(bankNames, cbccNameToCode));
                    setCbccCardTenure(tenureStr);
                    break;
                case PaymentInstrumentType.BFL_CARD:
                    setSelectedBflCards(normalizeToCodes(bankNames, bflNameToCode));
                    setBflCardTenure(tenureStr);
                    break;
            }
        }
    // Note: cardConfig intentionally excluded from deps — it's derived from config and the
    // loading gate in ModifyCampaignPage guarantees it's available when initialData arrives.
    }, [mode, initialData, campaignType]);

    // When config is null (view mode), derive card options from selected values
    // so that the NcemiBankCardSection can display the campaign's saved selections.
    const creditCardOptions = useMemo(
        () => cardConfig?.creditCard?.codes
            ? Object.entries(cardConfig.creditCard.codes)
            : selectedCreditCards.map((code) => [code, code] as [string, string]),
        [cardConfig, selectedCreditCards],
    );
    const debitCardOptions = useMemo(
        () => cardConfig?.debitCard?.codes
            ? Object.entries(cardConfig.debitCard.codes)
            : selectedDebitCards.map((code) => [code, code] as [string, string]),
        [cardConfig, selectedDebitCards],
    );
    const cbccCardOptions = useMemo(
        () => cardConfig?.cbccCard?.codes
            ? Object.entries(cardConfig.cbccCard.codes)
            : selectedCbccCards.map((code) => [code, code] as [string, string]),
        [cardConfig, selectedCbccCards],
    );
    const bflCardOptions = useMemo(
        () => cardConfig?.bflCard?.codes
            ? Object.entries(cardConfig.bflCard.codes)
            : selectedBflCards.map((code) => [code, code] as [string, string]),
        [cardConfig, selectedBflCards],
    );

    const creditTenureOptions: SelectProps.Option[] = useMemo(
        () => cardConfig?.creditCard?.allowedTenures
            ? Object.entries(cardConfig.creditCard.allowedTenures).map(
                ([tenureKey, tenureLabel]) => ({ label: tenureLabel, value: tenureKey }),
            )
            : creditCardTenure ? [{ label: creditCardTenure, value: creditCardTenure }] : [],
        [cardConfig, creditCardTenure],
    );
    const debitTenureOptions: SelectProps.Option[] = useMemo(
        () => cardConfig?.debitCard?.allowedTenures
            ? Object.entries(cardConfig.debitCard.allowedTenures).map(
                ([tenureKey, tenureLabel]) => ({ label: tenureLabel, value: tenureKey }),
            )
            : debitCardTenure ? [{ label: debitCardTenure, value: debitCardTenure }] : [],
        [cardConfig, debitCardTenure],
    );
    const cbccTenureOptions: SelectProps.Option[] = useMemo(
        () => cardConfig?.cbccCard?.allowedTenures
            ? Object.entries(cardConfig.cbccCard.allowedTenures).map(
                ([tenureKey, tenureLabel]) => ({ label: tenureLabel, value: tenureKey }),
            )
            : cbccCardTenure ? [{ label: cbccCardTenure, value: cbccCardTenure }] : [],
        [cardConfig, cbccCardTenure],
    );
    const bflTenureOptions: SelectProps.Option[] = useMemo(
        () => cardConfig?.bflCard?.allowedTenures
            ? Object.entries(cardConfig.bflCard.allowedTenures).map(
                ([tenureKey, tenureLabel]) => ({ label: tenureLabel, value: tenureKey }),
            )
            : bflCardTenure ? [{ label: bflCardTenure, value: bflCardTenure }] : [],
        [cardConfig, bflCardTenure],
    );

    // ── Brand name options ──────────────────────────────────────────────
    const brandNameOptions: SelectProps.Option[] = useMemo(() => {
        if (cardConfig?.brandToOfaMapping) {
            return Object.keys(cardConfig.brandToOfaMapping).map(
                (name) => ({ label: name, value: name }),
            );
        }
        // In view mode without config, derive from selected brand name
        if (brandName) return [{ label: brandName, value: brandName }];
        return [];
    }, [cardConfig, brandName]);

    return {
        sellerShare,
        setSellerShare,
        brandShare,
        setBrandShare,
        bankShare,
        setBankShare,
        amazonShare,
        setAmazonShare,
        brandName,
        setBrandName,
        brandNameOptions,
        selectedCreditCards,
        setSelectedCreditCards,
        selectedDebitCards,
        setSelectedDebitCards,
        selectedCbccCards,
        setSelectedCbccCards,
        selectedBflCards,
        setSelectedBflCards,
        creditCardTenure,
        setCreditCardTenure,
        debitCardTenure,
        setDebitCardTenure,
        cbccCardTenure,
        setCbccCardTenure,
        bflCardTenure,
        setBflCardTenure,
        priceLeadingGuardrail,
        setPriceLeadingGuardrail,
        creditCardOptions,
        debitCardOptions,
        cbccCardOptions,
        bflCardOptions,
        creditTenureOptions,
        debitTenureOptions,
        cbccTenureOptions,
        bflTenureOptions,
    };
}
